import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { Tab, Tabs } from '@mui/material';
import { clsx } from 'clsx';
import { CreatorLink } from './CreatorLink';
import { HomeVMVDispatcher } from '../home.viewModel';
import { Tag } from '@design-system/components/tag/tag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { LinksTab, Collection, Collections } from '../home.domain';
import { CreatorLinksType } from '../mappers';

type CreatorLinksProps = {
  dispatch: HomeVMVDispatcher;
  showLinks: boolean;
  activeTab: LinksTab;
  subscription: {
    show: boolean;
    links: CreatorLinksType;
  };
  otp: {
    show: boolean;
    options: Collections;
    selectedId?: number;
    links: CreatorLinksType;
  };
};

const linkTabs = [
  {
    label: 'Subscription',
    value: 'subscription',
  },
  {
    label: 'One-time Purchase',
    value: 'otp',
  },
];

const getTabs = (showOtp: boolean, showSubscription: boolean) => {
  if (showOtp && showSubscription) return linkTabs;
  if (showSubscription) return [linkTabs[0]];
  if (showOtp) return [linkTabs[1]];
  return [];
};

export const CreatorLinks = ({
  activeTab,
  showLinks,
  subscription,
  otp,
  dispatch,
}: CreatorLinksProps) => {
  if (!showLinks) return null;
  const tabs = getTabs(otp.show, subscription.show);

  return (
    <div className="bg-white rounded-[10px] p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Typography variant="title">Start selling</Typography>
        <a
          className={clsx(
            'flex gap-2 items-center',
            sprinkles({ color: 'P-80' }),
          )}
          href="https://playbookapp.io/handbook/marketing-habit"
          target="_blank"
          rel="noreferrer"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          <Typography>How to market your app</Typography>
        </a>
      </div>

      <div className="p-2 rounded-[10px]">
        <Typography>
          Please make sure to say <b>“go to my app, click the link”</b> instead
          of “go to Playbook.” To ensure you get attribution, direct users to
          sign up through your links rather than searching for Playbook on the
          App Store.{' '}
          <b>
            Remember, you only earn revenue when subscribers sign up directly
            through your link.
          </b>
        </Typography>
      </div>

      <div>
        <Tabs
          value={activeTab}
          className="mb-4"
          onChange={(_, value) => dispatch('setLinkTab', value)}
        >
          {tabs.map(({ label, value }) => (
            <Tab
              key={label}
              value={value}
              label={<Typography className="text-black">{label}</Typography>}
            />
          ))}
        </Tabs>
        <Content
          activeTab={activeTab}
          otp={otp}
          subscription={subscription}
          dispatch={dispatch}
        />
      </div>
    </div>
  );
};

const Content = ({
  activeTab,
  subscription,
  otp,
  dispatch,
}: Omit<CreatorLinksProps, 'showLinks'>) => {
  if (activeTab === 'subscription') {
    return <SubscriptionLinksComponent links={subscription.links} />;
  }

  return <OTPLinks {...otp} optionClick={(p) => dispatch('selectOtp', p)} />;
};

const SubscriptionLinksComponent = ({ links }: { links: CreatorLinksType }) => {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {links.map((link) => (
        <CreatorLink {...link} key={link.title} />
      ))}
    </div>
  );
};

const OTPLinks = ({
  options,
  links,
  selectedId,
  optionClick,
}: {
  options: Collections;
  links: CreatorLinksType;
  selectedId?: number;
  optionClick: (p: Collection) => void;
}) => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2">
        {options.map((p) => (
          <Tag
            key={p.id}
            labelClassName="text-ellipsis whitespace-nowrap overflow-hidden max-w-[150px]"
            label={p.name}
            className={clsx(
              p.id !== selectedId &&
                sprinkles({
                  color: 'B-100',
                  bg: { hover: 'N-20', lightMode: 'N-20' },
                }),
              p.id === selectedId &&
                sprinkles({
                  color: 'W-100',
                  bg: { hover: 'B-100', lightMode: 'B-100' },
                }),
            )}
            onClick={() => optionClick(p)}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {links.map((link) => (
          <CreatorLink
            key={link.title}
            title={link.title}
            url={link.url || ''}
            urlClassName="max-w-[250px] lg:max-w-[220px] text-ellipsis whitespace-nowrap overflow-hidden"
          />
        ))}
      </div>
    </div>
  );
};
